import type { ConditionEvaluator } from "../../types/conditionEvaluators.ts";
import type {
  INumberArrayBinaryCondition,
  INumberArrayTernaryCondition,
  NumberArrayCondition,
} from "../../types/conditions/arrays/numbers.ts";
import { Operator } from "../../types/operators.ts";
import { createThrowConditionNotImplementedForEvaluator, getArrayValues, getSingleValue } from "../common.ts";

const throwNotImplemented = createThrowConditionNotImplementedForEvaluator("number[]");

const binaryConditionEvaluator: ConditionEvaluator<INumberArrayBinaryCondition> = (condition, resolve): boolean => {
  const subjectValues = getArrayValues("number", condition.subject, resolve);
  const objectValue = getSingleValue("number", condition.object, resolve);

  switch (condition.operator) {
    case Operator.Has:
      return subjectValues.indexOf(objectValue) > -1;

    case Operator.NotHas:
      return subjectValues.indexOf(objectValue) === -1;

    case Operator.First:
      return subjectValues.indexOf(objectValue) === 0;

    case Operator.Last:
      return subjectValues.indexOf(objectValue) === subjectValues.length - 1;

    default:
      return throwNotImplemented(condition);
  }
};

const ternaryConditionEvaluator: ConditionEvaluator<INumberArrayTernaryCondition> = (condition, resolve): boolean => {
  const subjectValues = getArrayValues("number", condition.subject, resolve);
  const left = getSingleValue("number", condition.left, resolve);
  const right = getSingleValue("number", condition.right, resolve);

  switch (condition.operator) {
    case Operator.Before: {
      const indexLeft = subjectValues.indexOf(left);
      const indexRight = subjectValues.indexOf(right);
      if (indexLeft === -1 || indexRight === -1) {
        return false;
      }
      return indexLeft < indexRight;
    }

    case Operator.After: {
      const indexLeft = subjectValues.indexOf(left);
      const indexRight = subjectValues.indexOf(right);
      if (indexLeft === -1 || indexRight === -1) {
        return false;
      }
      return indexLeft > indexRight;
    }

    default:
      return throwNotImplemented(condition);
  }
};

export const conditionEvaluator: ConditionEvaluator<NumberArrayCondition> = (condition, resolve): boolean => {
  switch (condition.operator) {
    case Operator.Has:
    case Operator.NotHas:
    case Operator.First:
    case Operator.Last:
      return binaryConditionEvaluator(condition, resolve);

    case Operator.Before:
    case Operator.After:
      return ternaryConditionEvaluator(condition, resolve);

    default:
      return throwNotImplemented(condition);
  }
};

export default conditionEvaluator;
